/**
 * Refine Auth Provider for AxonBOS Gateway — JWT-based authentication.
 *
 * Uses axon-gateway /auth/login, /auth/refresh, and /auth/me endpoints.
 * Stores tokens in localStorage for persistence across page reloads.
 *
 * Flow:
 * 1. Login → POST /auth/login → store access_token + refresh_token
 * 2. Check → GET /auth/me → verify token validity
 * 3. Logout → client-side token clear (no server logout endpoint)
 * 4. Identity → GET /auth/me → return user info for UI
 *
 * JWT payload: { sub, email, company_id, role, exp, iat, type }
 * Response envelope: { success, data, error, meta }
 */

import type { AuthProvider } from '@refinedev/core';
import axios from 'axios';

// ─── Token Storage Keys ────────────────────────────────────────────

const TOKEN_KEY = 'axon_token';
const REFRESH_TOKEN_KEY = 'axon_refresh_token';
const COMPANY_ID_KEY = 'axon_company_id';
const USER_KEY = 'axon_user';

// ─── Types ─────────────────────────────────────────────────────────

interface LoginParams {
  email: string;
  password: string;
}

/** User shape returned by GET /auth/me */
interface AxonUser {
  id: string;
  email: string;
  full_name: string;
  company_id: number;
  role: string; // 'admin' | 'analyst' | 'viewer'
}

/** Common API response envelope from axon-gateway */
interface ApiResponse<T> {
  success: boolean;
  data: T;
  error: { code: string; message: string } | null;
  meta: {
    request_id: string;
    [key: string]: unknown;
  };
}

interface AuthTokens {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
}

// ─── Auth Provider Factory ─────────────────────────────────────────

export function createAuthProvider(gatewayUrl: string): AuthProvider {
  const httpClient = axios.create({ baseURL: gatewayUrl });

  // Attach token to all requests
  httpClient.interceptors.request.use((config) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  return {
    /**
     * Login — POST /auth/login
     * Stores JWT tokens + company_id in localStorage.
     */
    login: async ({ email, password }: LoginParams) => {
      try {
        const { data: response } = await httpClient.post<ApiResponse<AuthTokens>>(
          '/auth/login',
          { email, password },
        );

        if (!response.success || !response.data) {
          return {
            success: false,
            error: {
              name: 'LoginError',
              message: response.error?.message ?? 'Login failed',
            },
          };
        }

        const { access_token, refresh_token } = response.data;

        localStorage.setItem(TOKEN_KEY, access_token);
        localStorage.setItem(REFRESH_TOKEN_KEY, refresh_token);

        // Fetch and cache user info (includes company_id)
        try {
          const { data: meResponse } = await httpClient.get<ApiResponse<AxonUser>>(
            '/auth/me',
            { headers: { Authorization: `Bearer ${access_token}` } },
          );

          if (meResponse.success && meResponse.data) {
            localStorage.setItem(USER_KEY, JSON.stringify(meResponse.data));
            localStorage.setItem(
              COMPANY_ID_KEY,
              String(meResponse.data.company_id),
            );
          }
        } catch {
          // Non-fatal — user info will be fetched on next getIdentity call
        }

        return {
          success: true,
          redirectTo: '/',
        };
      } catch (error: any) {
        const apiError = error.response?.data;
        const message =
          apiError?.error?.message ??
          'Invalid email or password';

        return {
          success: false,
          error: {
            name: 'LoginError',
            message,
          },
        };
      }
    },

    /**
     * Logout — client-side only (axon-gateway has no logout endpoint).
     * Clears all stored tokens and user data.
     */
    logout: async () => {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(REFRESH_TOKEN_KEY);
      localStorage.removeItem(COMPANY_ID_KEY);
      localStorage.removeItem(USER_KEY);

      return {
        success: true,
        redirectTo: '/login',
      };
    },

    /**
     * Check — GET /auth/me
     * Verifies the stored token is still valid.
     * If expired, attempts a token refresh.
     */
    check: async () => {
      const token = localStorage.getItem(TOKEN_KEY);

      if (!token) {
        return {
          authenticated: false,
          redirectTo: '/login',
        };
      }

      try {
        await httpClient.get('/auth/me');
        return { authenticated: true };
      } catch (error: any) {
        // Token expired — try refresh
        if (error.response?.status === 401) {
          const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
          if (refreshToken) {
            try {
              const { data: response } = await httpClient.post<ApiResponse<AuthTokens>>(
                '/auth/refresh',
                { refresh_token: refreshToken },
              );

              if (response.success && response.data) {
                localStorage.setItem(TOKEN_KEY, response.data.access_token);
                localStorage.setItem(
                  REFRESH_TOKEN_KEY,
                  response.data.refresh_token,
                );
                return { authenticated: true };
              }
            } catch {
              // Refresh failed — force re-login
            }
          }
        }

        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(REFRESH_TOKEN_KEY);

        return {
          authenticated: false,
          redirectTo: '/login',
        };
      }
    },

    /**
     * OnError — handle 401/403 from data provider
     */
    onError: async (error: any) => {
      const status = error?.statusCode ?? error?.response?.status;

      if (status === 401) {
        return {
          logout: true,
          redirectTo: '/login',
          error,
        };
      }

      if (status === 403) {
        return {
          error: {
            ...error,
            message: 'You do not have permission to perform this action.',
          },
        };
      }

      return { error };
    },

    /**
     * GetIdentity — returns user info for the header/avatar.
     * Uses cached data first, falls back to GET /auth/me.
     */
    getIdentity: async () => {
      const cachedUser = localStorage.getItem(USER_KEY);

      if (cachedUser) {
        try {
          const user: AxonUser = JSON.parse(cachedUser);
          return {
            id: user.id,
            name: user.full_name || user.email,
            email: user.email,
            avatar: undefined, // axon-gateway doesn't serve avatars yet
          };
        } catch {
          // Corrupted cache — fetch fresh
        }
      }

      try {
        const { data: response } = await httpClient.get<ApiResponse<AxonUser>>('/auth/me');

        if (response.success && response.data) {
          const user = response.data;
          localStorage.setItem(USER_KEY, JSON.stringify(user));
          localStorage.setItem(COMPANY_ID_KEY, String(user.company_id));

          return {
            id: user.id,
            name: user.full_name || user.email,
            email: user.email,
            avatar: undefined,
          };
        }
        return null;
      } catch {
        return null;
      }
    },

    /**
     * GetPermissions — return user role for RBAC.
     * AxonBOS roles: 'admin', 'analyst', 'viewer'
     */
    getPermissions: async () => {
      const cachedUser = localStorage.getItem(USER_KEY);
      if (cachedUser) {
        try {
          const user: AxonUser = JSON.parse(cachedUser);
          return user.role;
        } catch {
          return null;
        }
      }
      return null;
    },
  };
}

// ─── Utility Exports ───────────────────────────────────────────────

/** Get the current company_id from localStorage */
export function getCompanyId(): number | null {
  const id = localStorage.getItem(COMPANY_ID_KEY);
  return id ? parseInt(id, 10) : null;
}

/** Get the current access token from localStorage */
export function getAccessToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

/** Get the cached user from localStorage */
export function getCachedUser(): AxonUser | null {
  const cached = localStorage.getItem(USER_KEY);
  if (!cached) return null;
  try {
    return JSON.parse(cached);
  } catch {
    return null;
  }
}
